// @ts-check
import React, { Component } from 'react';
import { Page, Row, Widget } from 'shared/containers/portal_layout';
import LoadingContent from 'shared/components/LoadingContent';
import buildActions from './actions';
import { Form } from 'utils/reduxFormWrapper';
import HForm, { SelectField } from 'shared/containers/portal_layout/HForm';
import Errors from 'shared/components/Errors';
import FlashMessageActions from 'shared/actions/FlashMessageActions';
import PromiseButton from 'shared/components/PromiseButton';
import SpinnerButton from 'shared/components/SpinnerButton';
import { Link } from 'react-router';

class Show extends Component {
  constructor(props, context) {
    super(props, context);
    this.state = { payers: null, payerRejections: null, plans: null, planRejections: null };
    this.removePayerRejection = this.removePayerRejection.bind(this);
    this.createPayerRejection = this.createPayerRejection.bind(this);
    this.removePlanRejection = this.removePlanRejection.bind(this);
    this.createPlanRejection = this.createPlanRejection.bind(this);
    this.actions = buildActions(props.apiPrefix);
  }

  componentDidMount() {
    this.refresh();
  }

  refresh() {
    this.actions.fetchInsuranceRejections().then(data => this.setState({ ...data }));
  }

  removePayerRejection(pr) {
    /* eslint-disable no-alert */
    if (window.confirm(`Are you sure you want to un-restrict ${pr.payer.name}?`)) {
      return this.actions.removePayerRejection(pr.id).then(response => {
        FlashMessageActions.flashSuccess({ message: 'Payer removed from Restricted List' });
        this.refresh();
        return null;
      });
    }
    return Promise.resolve();
    /* eslint-enable no-alert */
  }

  createPayerRejection(formValues) {
    return this.actions.createPayerRejection(formValues).then(response => {
      FlashMessageActions.flashSuccess({ message: 'Payer added to Restricted List' });
      this.refresh();
      return null;
    });
  }

  removePlanRejection(pr) {
    /* eslint-disable no-alert */
    if (window.confirm(`Are you sure you want to un-restrict ${pr.plan.name}?`)) {
      return this.actions.removePlanRejection(pr.id).then(response => {
        FlashMessageActions.flashSuccess({ message: 'Plan removed from Restricted List' });
        this.refresh();
        return null;
      });
    }
    return Promise.resolve();
    /* eslint-enable no-alert */
  }

  createPlanRejection(formValues) {
    return this.actions.createPlanRejection(formValues).then(response => {
      FlashMessageActions.flashSuccess({ message: 'Plan added to Restricted List' });
      this.refresh();
      return null;
    });
  }

  render() {
    if (!this.state.payers) return <LoadingContent />;
    return (
      <PresentationComponent
        {...this.state}
        removePayerRejection={this.removePayerRejection}
        createPayerRejection={this.createPayerRejection}
        removePlanRejection={this.removePlanRejection}
        createPlanRejection={this.createPlanRejection}
        entityId={this.props.entityId}
      />
    );
  }
}

export default Show;

const PresentationComponent = ({
  entityId,
  payers,
  plans,
  planRejections,
  payerRejections,
  removePayerRejection,
  createPayerRejection,
  removePlanRejection,
  createPlanRejection,
}) => (
  <Page title="Manage Unaccepted Insurance Providers/Plans">
    <Row>
      <Widget icon="pencil" title="Restrict incoming payers - Unaccepted Payers" span={6}>
        {payerRejections.length === 0 ? (
          <div className="well">
            <p>
              {"You don't currently have any insurance/payer restrictions on incoming patients."}
            </p>
          </div>
        ) : (
          <div className="rejected-payers-list">
            <h5>Rejected Payers</h5>
            <table className="table table-striped table-bordered table-hover">
              <thead>
                <tr>
                  <th>Payer Name</th>
                  <th>Remove restriction?</th>
                </tr>
              </thead>
              <tbody>
                {payerRejections.map(pr => (
                  <tr key={pr.id}>
                    <td>{pr.payer.name}</td>
                    <td>
                      {!pr.rejectingEntity || pr.rejectingEntity.id === entityId ? (
                        <PromiseButton
                          type="button"
                          className="btn btn-mini btn-danger"
                          data-spec-id={`remove_from_restricted_payers_${pr.id}`}
                          onClick={() => removePayerRejection(pr)}
                        >
                          un-restrict payer access
                        </PromiseButton>
                      ) : (
                        <p>
                          Rejected by <RejectingEntityLink entity={pr.rejectingEntity} />
                        </p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Form
          form="restrictPayer"
          onSubmit={createPayerRejection}
          content={props => (
            <HForm onSubmit={props.handleSubmit}>
              <Errors errors={props.error || []} />
              <SelectField
                label="Don't accept patients of payer:"
                name="restrictedPayerId"
                options={payers}
                valueKey="id"
                labelKey="name"
              />
              <div className="form-actions">
                <SpinnerButton type="submit" className="btn btn-primary" loading={props.submitting}>
                  Restrict Payer
                </SpinnerButton>
              </div>
            </HForm>
          )}
        />
      </Widget>

      <Widget icon="pencil" title="Restrict specific plans - Unaccepted Insurance Plans" span={6}>
        {planRejections.length === 0 ? (
          <div className="well">
            <p>
              {
                "You don't currently have any specific insurance plan restrictions on incoming patients."
              }
            </p>
          </div>
        ) : (
          <div className="rejected-plans-list">
            <h5>Rejected Insurance Plans</h5>
            <table className="table table-striped table-bordered table-hover">
              <thead>
                <tr>
                  <th>Plan Name</th>
                  <th>Remove restriction?</th>
                </tr>
              </thead>
              <tbody>
                {planRejections.map(pr => (
                  <tr key={pr.id}>
                    <td>{pr.plan.name}</td>
                    <td>
                      {!pr.rejectingEntity || pr.rejectingEntity.id === entityId ? (
                        <PromiseButton
                          type="button"
                          className="btn btn-mini btn-danger"
                          data-spec-id={`remove_from_restricted_plans_${pr.id}`}
                          onClick={() => removePlanRejection(pr)}
                        >
                          un-restrict plan access
                        </PromiseButton>
                      ) : (
                        <p>
                          Rejected by <RejectingEntityLink entity={pr.rejectingEntity} />
                        </p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Form
          form="restrictPlan"
          onSubmit={createPlanRejection}
          content={props => (
            <HForm onSubmit={props.handleSubmit}>
              <Errors errors={props.error || []} />
              <SelectField
                label="Don't accept patients with insurance plan:"
                name="restrictedPlanId"
                options={plans}
                valueKey="id"
                labelKey="name"
              />
              <div className="form-actions">
                <SpinnerButton type="submit" className="btn btn-primary" loading={props.submitting}>
                  Restrict Plan
                </SpinnerButton>
              </div>
            </HForm>
          )}
        />
      </Widget>
    </Row>
  </Page>
);

const RejectingEntityLink = ({ entity }) => {
  switch (entity.type) {
    case 'Location':
      return <Link to={`/admin/portal/locations/${entity.id}`}>{entity.name}</Link>;
    case 'Organization':
      return <Link to={`/admin/portal/organizations/${entity.id}`}>{entity.name}</Link>;
    default:
      return <span>Unknown</span>;
  }
};
